import { Link, createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useMe } from '../auth';
import { useCases, useCreateCase } from '../cases';
import { useLocale } from '../locale';
import { m } from '../paraglide/messages';
import { rootRoute } from './root';

export const casesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases',
  component: CasesRoute,
});

function CasesRoute() {
  const me = useMe();
  const navigate = useNavigate();
  const cases = useCases();
  const create = useCreateCase();

  // Anonymous users go to /login.
  useEffect(() => {
    if (!me.isLoading && !me.data?.user) {
      navigate({ to: '/login', replace: true });
    }
  }, [me.isLoading, me.data, navigate]);

  const [locale] = useLocale();

  if (me.isLoading || cases.isLoading) {
    return <p className="centered muted">{m.caseLoading()}</p>;
  }

  const onCreate = () => {
    create.mutate(
      { title: undefined },
      {
        onSuccess: ({ case: c }) => {
          navigate({ to: '/cases/$id', params: { id: c.id }, search: { log: undefined } });
        },
      },
    );
  };

  // Locale-driven date formatting — replaces hardcoded 'en-GB'.
  // 'pt' → pt-PT (DD/MM/YYYY), 'en' → en-GB (DD/MM/YYYY) which is the
  // expected EU date order even on the EN UI.
  const dateFmt = new Intl.DateTimeFormat(locale === 'pt' ? 'pt-PT' : 'en-GB');

  const list = cases.data ?? [];

  return (
    <div className="card">
      <header className="card-head">
        <h1>{m.casesListHeading()}</h1>
        <button type="button" onClick={onCreate} disabled={create.isPending}>
          {create.isPending ? m.loginSubmitting() : m.casesListNew()}
        </button>
      </header>

      {create.isError ? (
        <div className="notice error">
          {create.error && typeof create.error === 'object' && 'message' in create.error
            ? String((create.error as { message?: unknown }).message ?? 'Could not create case.')
            : 'Could not create case.'}
        </div>
      ) : null}

      {list.length === 0 ? (
        <div className="empty">
          <p>{m.casesListEmpty()}</p>
          <p className="muted small">{m.casesListEmptyHint()}</p>
        </div>
      ) : (
        <ul className="cases">
          {list.map((c) => (
            <li key={c.id}>
              <Link
                to="/cases/$id"
                params={{ id: c.id }}
                search={{ log: undefined }}
                className="case-row"
              >
                <div className="case-title">{c.title ?? m.casesListUntitled()}</div>
                <div className="case-row-meta">
                  <span className={`badge status-${c.status}`}>{c.status}</span>
                  <span className="muted small">{dateFmt.format(new Date(c.created_at))}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
